import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { CSVLink } from 'react-csv';

import {
    Autosuggest,
    Button,
    Container,
    Header,
    Flashbar,
    FlashbarProps,
    SpaceBetween,
    Select,
    DateRangePicker,
    Box,
    Link,
    FormField,
    Table,
    Popover,
    Pagination,
    DateRangePickerProps,
    ToggleButton,
} from '@amzn/awsui-components-react';
import * as awsui from '@amzn/awsui-design-tokens';

import Styled from './styled';

import {
    datePickerI18nStrings,
    isValidRange,
    relativeDateOptions,
} from '../../../common/ComponentUtils';
import { useEmployeeData, UserRole } from '../../../common/EmployeeContext';
import { UserPermissions } from '../../../common/UserPermissions';
import {
    fetchReviews,
    fetchReviewDetails,
    updateReview,
} from '../api';
import {
    CoachingStatus,
    CsqaDashboardProps,
    FetchReviewsParams,
    GetCsqaPayload,
    ReviewFetchResponse,
    SearchCsqaPayload,
} from '../types';
import { useCollection } from '@amzn/awsui-collection-hooks';
import ViewDetailsModal from './ViewDetails';
import EditReviewModal from './EditReview';

interface OptionDefinition {
    label: string;
    value: string;
}

const CsqaDashboard: React.FC<CsqaDashboardProps> = ({ org, role, user, userPermissions }) => {
    // State variables
    const [allReviews, setAllReviews] = useState<SearchCsqaPayload[]>([]);
    const [displayedReviews, setDisplayedReviews] = useState<SearchCsqaPayload[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [searchCollapsed, setSearchCollapsed] = useState(false);

    // Filters (server-side)
    const [associateEmail, setAssociateEmail] = useState<string>('');
    const [evaluatorEmail, setEvaluatorEmail] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [associateSite, setAssociateSite] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [supervisorEmail, setSupervisorEmail] = useState<string>('');
    const [dateRange, setDateRange] = useState<DateRangePickerProps.Value | null>(null);

    // Flters (client-side)
    const [selectedFormId, setSelectedFormId] = useState<string>('');
    const [selectedEvaluationType, setSelectedEvaluationType] = useState<string>('');
    const [selectedCoachingStatus, setSelectedCoachingStatus] = useState<string>('');

    // Alert (banner)
    const [flashbarItems, setFlashbarItems] = useState<FlashbarProps.MessageDefinition[]>([]);

    // Viewing and Editing Details
    const [detailLoading, setDetailLoading] = useState<boolean>(false);
    const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
    const [selectedReview, setSelectedReview] = useState<GetCsqaPayload | null>(null);
    const [viewDetailsModalOpen, setViewDetailsModalOpen] = useState<boolean>(false);

    const {
        agents,
        leads,
        supervisors,
        loading: employeesLoading,
        error: employeesError
    } = useEmployeeData();

    const associatesList = useMemo(() => {
        if (userPermissions.has(UserPermissions.CreateCsqa) || userPermissions.has(UserPermissions.SearchCsqa)) {
            const combined = [...agents, ...leads];
            return combined.sort((a, b) => a.Username.localeCompare(b.Username));
        }
        return agents;
    }, [
        agents,
        leads,
        userPermissions,
    ]);

    if (employeesError) {
        setFlashbarItems([{
            type: 'error',
            content: (
                <><b>Unable to retrieve hierarchy details</b> please refresh your page to try again</>
            ),
            dismissible: true,
            onDismiss: () => setFlashbarItems([]),
            id: 'max-results-error'
        }]);
    }

    const addFlashbarItem = (type: FlashbarProps.Type, content: React.ReactNode, id: string) => {
        setFlashbarItems((prevItems) => [
            ...prevItems,
            {
                type,
                content,
                dismissible: true,
                onDismiss: () => setFlashbarItems((items) => items.filter(item => item.id !== id)),
                id,
            },
        ]);
    };

    const addSuccessMessage = (content: React.ReactNode, id: string = `success-${Date.now()}`) => {
        addFlashbarItem('success', content, id);
    };

    const addErrorMessage = (content: React.ReactNode, id: string = `error-${Date.now()}`) => {
        addFlashbarItem('error', content, id);
    };

    interface ReviewFetchParams {
        startDate?: string;
        endDate?: string;
        associateEmailFilter?: string;
        supervisorEmailFilter?: string;
        evaluatorEmailFilter?: string;
        associateSiteFilter?: string;
        viewAll?: boolean;
    }

    const reviewFetch = useCallback(async (params: ReviewFetchParams) => {
        const {
            startDate,
            endDate,
            associateEmailFilter,
            supervisorEmailFilter,
            evaluatorEmailFilter,
            associateSiteFilter,
            viewAll,
        } = params;
        setLoading(true);
        setFlashbarItems([]);
        try {
            const results = await fetchReviews(
                startDate,
                endDate,
                associateEmailFilter,
                supervisorEmailFilter,
                evaluatorEmailFilter,
                associateSiteFilter,
                viewAll,
            ) as ReviewFetchResponse;
            if (results) {
                setAllReviews(results.payload);
                if (!!results.maxResults) {
                    setFlashbarItems([{
                        type: 'error',
                        content: (
                            <>Max Results Displayed, consider reducing Date Range</>
                        ),
                        dismissible: true,
                        onDismiss: () => setFlashbarItems([]),
                        id: 'max-results-error'
                    }]);
                }
            }
        } catch (e: unknown) {
            console.error('There was a problem with your Filter Request', e);
        } finally {
            setLoading(false);
        }
    }, []);

    // Define default table filters based on permissions/role
    const defaultFilter = useMemo<FetchReviewsParams | null>(() => {
        const rules: Array<[() => boolean, FetchReviewsParams]> = [
            // Managers (CSQA Manager, Admin)
            [() => userPermissions.has(UserPermissions.SearchCsqa), { viewAll: true }],
            // CSQA agents
            [() => userPermissions.has(UserPermissions.SearchAgentCsqa), { evaluatorEmailFilter: user }],
            // OM / SeniorOM 
            [() => role !== undefined && [UserRole.Om, UserRole.SeniorOm].includes(role),
            { associateSiteFilter: org }],
            // Supervisor
            [() => role === UserRole.Supervisor,
            { supervisorEmailFilter: user }],
            // Lead/Agent
            [() => role !== undefined && [UserRole.Lead, UserRole.Agent].includes(role),
            { associateEmailFilter: user }],
        ];

        const found = rules.find(([predicate]) => predicate());
        return found ? found[1] : null;
    }, [org, role, user, userPermissions]);

    // ensure we only re-run reviewFetch when defaultFilter is defined/changes with meaningful values
    const hasDefaultFilterValue = useMemo(() => {
        if (!defaultFilter) return false;
        // only fire if one of these is truthy
        return !!(
            defaultFilter.viewAll ||
            defaultFilter.associateEmailFilter ||
            defaultFilter.supervisorEmailFilter ||
            defaultFilter.evaluatorEmailFilter ||
            defaultFilter.associateSiteFilter
        );
    }, [defaultFilter]);

    const defaultReviewFetch = useCallback(() => {
        if (!defaultFilter) return;
        reviewFetch(defaultFilter);
    }, [defaultFilter, reviewFetch]);

    // On load: fetch reviews
    useEffect(() => {
        if (!hasDefaultFilterValue || !defaultFilter) {
            // No matching rule (Display No Data)
            setAllReviews([]);
            setDisplayedReviews([]);
            setLoading(false);
            return;
        }

        // Set default filter
        setAssociateEmail(defaultFilter.associateEmailFilter || '');
        setSupervisorEmail(defaultFilter.supervisorEmailFilter || '');
        setEvaluatorEmail(defaultFilter.evaluatorEmailFilter || '');
        setAssociateSite(defaultFilter.associateSiteFilter || '');
        setStartDate('');
        setEndDate('');

        reviewFetch(defaultFilter)
    }, [
        hasDefaultFilterValue,
        reviewFetch,
    ]);

    // Unique formIds, review types, and coaching statuses from displayed data (or from allReviews)
    const uniqueFormIds = useMemo(() => {
        const setIds = new Set(allReviews.map(r => r.formId ?? '').filter(f => f));
        return Array.from(setIds).sort();
    }, [allReviews]);

    const uniqueEvaluationTypes = useMemo(() => {
        const setTypes = new Set(allReviews.map(r => r.evaluationType ?? '').filter(f => f));
        return Array.from(setTypes).sort();
    }, [allReviews]);

    const uniqueCoachingStatus = useMemo(() => {
        const setTypes = new Set(allReviews.map(r => r.coachingStatus ?? '').filter(f => f));
        return Array.from(setTypes).sort();
    }, [allReviews]);

    // Handle filtering logic whenever value changes
    useEffect(() => {
        let filtered = allReviews;
        if (selectedFormId) {
            filtered = filtered.filter(r => (r.formId ?? '').toLowerCase().includes(selectedFormId.toLowerCase()));
        }
        if (selectedEvaluationType) {
            filtered = filtered.filter(r => (r.evaluationType ?? '') === selectedEvaluationType);
        }
        if (selectedCoachingStatus) {
            filtered = filtered.filter(r => (r.coachingStatus ?? '') === selectedCoachingStatus);
        }
        setDisplayedReviews(filtered);
    }, [selectedFormId, selectedCoachingStatus, selectedEvaluationType, allReviews]);

    const handleViewDetails = useCallback(async (review: SearchCsqaPayload) => {
        if (!review.id || !review.createdTimestamp) {
            console.error('Review ID or createdTimestmap is missing');
            addErrorMessage('Cannot load review details due to missing information.', 'missing-info-error');
            return;
        }
        setSelectedReview(null);
        setViewDetailsModalOpen(true);
        setDetailLoading(true);
        try {
            const detailedData = await fetchReviewDetails(review.id, review.createdTimestamp);
            setDetailLoading(false);
            if (detailedData) {
                setSelectedReview(detailedData);
            } else {
                addErrorMessage('Failed to load review details. Please try again.', 'fetch-details-error');
            }
        } catch (error) {
            setDetailLoading(false);
            addErrorMessage('An error occurred while fetching review details.', 'fetch-details-error');
        }
    }, []);

    const handleEditReview = useCallback(() => {
        setViewDetailsModalOpen(false);
        setEditModalOpen(true);
    }, []);

    const handleDeleteReview = useCallback(async () => {
        if (!selectedReview) {
            console.log('no review selected, nothing to delete');
            return;
        }
        if (window.confirm('Selecting confirm below will Delete this Review from the Dashboard. Are you sure?')) {
            try {
                // Set deleted to true
                const payload = {
                    doc: {
                        ...selectedReview,
                        identifiers: {
                            ...selectedReview.identifiers,
                            deleted: true,
                        },
                        editReason: 'Delete review',
                        updateType: 'edit',
                    },
                    id: selectedReview.id
                };
                await updateReview(payload);
                addSuccessMessage('Review deleted successfully.', 'delete-success');
                // Refresh the list after deletion
                defaultReviewFetch();
                setViewDetailsModalOpen(false);
            } catch (error) {
                addErrorMessage('Failed to delete the review. Please try again.', 'delete-error');
            }
        }
    }, [defaultReviewFetch, selectedReview]);

    const handleCsqaFormEditSubmit = useCallback(async (updatedData: Partial<GetCsqaPayload>) => {
        if (!selectedReview || !selectedReview.id) return;
        const payload = {
            doc: {
                ...selectedReview,
                ...updatedData,
                identifiers: {
                    ...selectedReview.identifiers,
                    evaluatorEmail: selectedReview.identifiers.evaluatorEmail,
                    evaluationDate: selectedReview.identifiers.evaluationDate,
                },
                editReason: 'Dispute',
                updateType: 'edit'
            },
            id: selectedReview.id,
        };
        try {
            await updateReview(payload);
            addSuccessMessage('Success! Your changes are now showing in the Dashboard.', 'update-success');
            setEditModalOpen(false);
            // Refresh the list after update
            defaultReviewFetch();
        } catch (error) {
            addErrorMessage('There was a problem updating the review. Please try again.', 'update-error');
        }
    }, [selectedReview, user, defaultReviewFetch]);

    const coachedByList = useMemo(() => {
        const users = allReviews.map((item) => item.evaluatorEmail ?? '');
        return Array.from(new Set(users.filter(u => u !== ''))).sort();
    }, [allReviews]);

    const siteList = useMemo(() => {
        const sites = associatesList.map((item) => item.Site);
        return Array.from(new Set(sites)).sort();
    }, [associatesList]);

    const collectionOptions = useMemo(() => ({
        pagination: {
            pageSize: searchCollapsed ? 20 : 10,
        },
        selection: {},
        filtering: {},
    }), [searchCollapsed]);

    const { items, paginationProps } = useCollection(displayedReviews, collectionOptions);

    const exportReviewsCsvContent = useMemo(() => allReviews, [allReviews]);

    const reviewsCsvFilename = useMemo(() => {
        const curDate = new Date();
        return `CSQA_Export_${(curDate.getMonth() + 1).toString().padStart(2, '0')}_${curDate.getDate().toString().padStart(2, '0')}_${curDate.getFullYear()}.csv`;
    }, [allReviews]);

    const agentListOptions = useMemo(() => associatesList.map(agent => ({ label: agent.Username, value: agent.Username })), [associatesList]);
    const supervisorListOptions = useMemo(() => supervisors.map(sup => ({ label: sup.Username, value: sup.Username })), [supervisors]);
    const coachedByListOptions = useMemo(() => coachedByList.map(u => ({ label: u, value: u })), [coachedByList]);
    const siteListOptions = useMemo(() => siteList.map(s => ({ label: s, value: s })), [siteList]);
    const formIdOptions = useMemo(() => uniqueFormIds.map(id => ({ label: id, value: id })), [uniqueFormIds]);
    const evaluationTypeOptions = useMemo(() =>
        uniqueEvaluationTypes.map(rt => ({
            label: String(rt),
            value: String(rt)
        } as OptionDefinition)),
        [uniqueEvaluationTypes]);
    const coachingStatusOptions = useMemo(() => uniqueCoachingStatus.map(cs => ({ label: cs, value: cs })), [uniqueCoachingStatus]);

    const columns = useMemo(() => {
        return [
            {
                id: 'actions',
                header: 'Actions',
                cell: (e: SearchCsqaPayload) => (
                    <SpaceBetween direction='vertical' size='xs'>
                        <Link onFollow={() => handleViewDetails(e)} variant='primary'>
                            View Details
                        </Link>
                    </SpaceBetween>
                ),
                width: 150,
                minWidth: 150,
            },
            {
                id: 'evaluationDate',
                header: 'Date',
                cell: (e: SearchCsqaPayload) => e.evaluationDate,
                sortingField: 'evaluationDate',
                width: 135,
                minWidth: 135,
            },
            {
                id: 'associateEmail',
                header: 'Associate Email',
                cell: (e: SearchCsqaPayload) => e.associateEmail,
                sortingField: 'associateEmail'
            },
            {
                id: 'supervisorEmail',
                header: 'Supervisor Email',
                cell: (e: SearchCsqaPayload) => e.supervisorEmail,
            },
            {
                id: 'evaluationType',
                header: 'Evaluation Type',
                cell: (e: SearchCsqaPayload) => e.evaluationType,
                width: 155,
                minWidth: 155,
            },
            {
                id: 'formId',
                header: 'Form ID',
                cell: (e: SearchCsqaPayload) => e.formId,
                width: 125,
                minWidth: 125,
            },
            {
                id: 'contactId',
                header: 'Contact ID',
                cell: (e: SearchCsqaPayload) => e.contactId,
            },
            {
                id: 'coachingStatus',
                header: 'Status',
                cell: (e: SearchCsqaPayload) => {
                    switch (e.coachingStatus) {
                        case CoachingStatus.Required:
                            return (
                                <Popover triggerType='text' content='Coaching Required' dismissButton={false}>
                                    ❗
                                </Popover>
                            );
                        case CoachingStatus.Complete:
                            return (
                                <Popover triggerType='text' content='Coaching Complete' dismissButton={false}>
                                    <span style={{ color: 'green' }}>✓</span>
                                </Popover>
                            );
                        default:
                            return null;
                    }
                },
                width: 100,
                minWidth: 100,
            }
        ];
    }, [handleViewDetails, handleDeleteReview, userPermissions]);

    return (
        <Styled.DashboardWrapper>
            {flashbarItems.length > 0 && <Flashbar items={flashbarItems} />}
            <Styled.DashboardBody>
                {/* Filter Container */}
                {!searchCollapsed &&
                    <Styled.HeaderWrapper>
                        <Styled.SearchWrapper>
                            <Container
                                header={<Header variant='h2'>Search</Header>}
                                className='filter-container'
                            >
                                <SpaceBetween size='m' direction='horizontal'>
                                    <FormField label='Date Range'>
                                        <DateRangePicker
                                            onChange={({ detail }) => {
                                                setDateRange(detail.value);
                                                if (detail.value && detail.value.type === 'absolute') {
                                                    setStartDate(detail.value.startDate || '');
                                                    setEndDate(detail.value.endDate || '');
                                                } else {
                                                    setStartDate('');
                                                    setEndDate('');
                                                }
                                            }}
                                            value={dateRange}
                                            relativeOptions={relativeDateOptions}
                                            isValidRange={isValidRange}
                                            i18nStrings={datePickerI18nStrings}
                                            dateOnly
                                            placeholder='Search by date range'
                                            rangeSelectorMode='absolute-only'
                                        />
                                        <Box variant='awsui-key-label' color='text-body-secondary'>
                                            Default Range | Last 30 Days
                                        </Box>
                                    </FormField>

                                    {(userPermissions.has(UserPermissions.SearchCsqa) || userPermissions.has(UserPermissions.SearchAgentCsqa)) && (
                                        <>
                                            <FormField label='Agent'>
                                                <Autosuggest
                                                    value={associateEmail}
                                                    onChange={e => setAssociateEmail(e.detail.value)}
                                                    onSelect={e => {
                                                        const selectedValue = e.detail.value;
                                                        const found = associatesList.find(h => h.Username === selectedValue);
                                                        if (found) {
                                                            setAssociateEmail(found.Username);
                                                            setSupervisorEmail('');
                                                            setAssociateSite('');
                                                            setEvaluatorEmail('');
                                                        }
                                                    }}
                                                    options={agentListOptions}
                                                    placeholder='Search by Agent'
                                                    filteringType='auto'
                                                />
                                            </FormField>
                                            <FormField label='Supervisor'>
                                                <Select
                                                    selectedOption={supervisorEmail ? { label: supervisorEmail, value: supervisorEmail } : null}
                                                    onChange={({ detail }) => {
                                                        setSupervisorEmail(detail.selectedOption.value || '');
                                                        setAssociateEmail('');
                                                        setAssociateSite('');
                                                        setEvaluatorEmail('');
                                                    }}
                                                    options={supervisorListOptions}
                                                    placeholder='Search by Supervisor'
                                                />
                                            </FormField>
                                            {userPermissions.has(UserPermissions.SearchCsqa) && (
                                                <FormField label='Site'>
                                                    <Select
                                                        selectedOption={associateSite ? { label: associateSite, value: associateSite } : null}
                                                        onChange={({ detail }) => {
                                                            setAssociateSite(detail.selectedOption.value || '');
                                                            setAssociateEmail('');
                                                            setSupervisorEmail('');
                                                            setEvaluatorEmail('');
                                                        }}
                                                        options={siteListOptions}
                                                        placeholder='Search by Site'
                                                    />
                                                </FormField>
                                            )}
                                            <FormField label='CSQA Agent'>
                                                <Select
                                                    selectedOption={evaluatorEmail ? { label: evaluatorEmail, value: evaluatorEmail } : null}
                                                    onChange={({ detail }) => {
                                                        setEvaluatorEmail(detail.selectedOption.value || '');
                                                        setAssociateEmail('');
                                                        setAssociateSite('');
                                                        setSupervisorEmail('');
                                                    }}
                                                    options={coachedByListOptions}
                                                    placeholder='Search by CSQA Agent'
                                                />
                                            </FormField>
                                        </>
                                    )}
                                </SpaceBetween>
                                <Styled.SearchSubmitWrapper>
                                    <SpaceBetween direction='horizontal' size='xs' alignItems='center'>
                                        <Button
                                            onClick={() => {
                                                if (defaultFilter) {
                                                    setAssociateEmail(defaultFilter.associateEmailFilter || '');
                                                    setSupervisorEmail(defaultFilter.supervisorEmailFilter || '');
                                                    setEvaluatorEmail(defaultFilter.evaluatorEmailFilter || '');
                                                    setAssociateSite(defaultFilter.associateSiteFilter || '');
                                                    setDateRange(null);
                                                    setStartDate('');
                                                    setEndDate('');
                                                    defaultReviewFetch();
                                                }
                                            }}
                                            variant='link'
                                        >
                                            Reset
                                        </Button>
                                        <Button
                                            variant='primary'
                                            onClick={() => {
                                                const isDateOnly = Boolean(
                                                    startDate &&
                                                    endDate &&
                                                    !associateEmail &&
                                                    !supervisorEmail &&
                                                    !evaluatorEmail &&
                                                    !associateSite
                                                );

                                                // If only date range is provided and no other filters, apply defaultFilter
                                                const searchParams: ReviewFetchParams = {
                                                    ...(startDate && endDate && { startDate, endDate }),
                                                    ...(associateEmail && { associateEmailFilter: associateEmail }),
                                                    ...(supervisorEmail && { supervisorEmailFilter: supervisorEmail }),
                                                    ...(evaluatorEmail && { evaluatorEmailFilter: evaluatorEmail }),
                                                    ...(associateSite && { associateSiteFilter: associateSite }),
                                                    ...(isDateOnly && defaultFilter ? defaultFilter : {}),
                                                };

                                                reviewFetch(searchParams);
                                            }}
                                        >
                                            Search
                                        </Button>
                                    </SpaceBetween>
                                </Styled.SearchSubmitWrapper>
                            </Container>
                        </Styled.SearchWrapper>
                        <Styled.FilterWrapper>
                            <Container
                                className='filter-container'
                                header={<Header variant='h2'>Filter</Header>}
                            >
                                <SpaceBetween size='m' direction='horizontal' alignItems='center'>
                                    <FormField label='Form ID'>
                                        <Autosuggest
                                            value={selectedFormId}
                                            onChange={e => setSelectedFormId(e.detail.value)}
                                            onSelect={e => setSelectedFormId(e.detail.value)}
                                            options={formIdOptions}
                                            placeholder='Filter by Form ID'
                                            filteringType='auto'
                                        />
                                    </FormField>
                                    <FormField label='Evaluation Type'>
                                        <Select
                                            selectedOption={selectedEvaluationType ? { label: selectedEvaluationType, value: selectedEvaluationType } : null}
                                            onChange={({ detail }) => {
                                                setSelectedEvaluationType(detail.selectedOption.value || '');
                                            }}
                                            options={[{ label: 'All', value: '' }, ...evaluationTypeOptions]}
                                            placeholder='Filter by Evaluation Type'
                                        />
                                    </FormField>
                                    <FormField label='Red Flag'>
                                        <Select
                                            selectedOption={selectedCoachingStatus ? { label: selectedCoachingStatus, value: selectedCoachingStatus } : null}
                                            onChange={({ detail }) => {
                                                setSelectedCoachingStatus(detail.selectedOption.value || '');
                                            }}
                                            options={[{ label: 'All', value: '' }, ...coachingStatusOptions]}
                                            placeholder='Filter by Review Type'
                                        />
                                    </FormField>
                                </SpaceBetween>
                                <Styled.FilterSubmitWrapper>
                                    <Button
                                        variant='link'
                                        onClick={() => {
                                            setSelectedFormId('');
                                            setSelectedCoachingStatus('');
                                            setSelectedEvaluationType('');
                                        }}
                                    >
                                        Clear
                                    </Button>
                                </Styled.FilterSubmitWrapper>
                            </Container>
                        </Styled.FilterWrapper>
                    </Styled.HeaderWrapper>
                }
                <Styled.DashboardTable>
                    <Container header={
                        <Styled.ContainerHeader>
                            <Styled.HeaderText>
                                <Header variant='h2'>CSQA Evaluations</Header>
                            </Styled.HeaderText>
                            <Styled.HeaderButtons>
                                {allReviews.length > 0 && (
                                    <Button variant='primary' wrapText={false}>
                                        <CSVLink
                                            filename={reviewsCsvFilename}
                                            data={exportReviewsCsvContent}
                                            style={{
                                                color: awsui.colorTextButtonPrimaryDefault,
                                                textDecoration: 'none',
                                            }}
                                        >
                                            Export to CSV
                                        </CSVLink>
                                    </Button>
                                )}
                                <ToggleButton
                                    ariaLabel={searchCollapsed ? 'Show Search Options' : 'Hide Search Options'}
                                    onChange={({ detail }) => setSearchCollapsed(detail.pressed)}
                                    pressed={searchCollapsed}
                                    iconName='subtract-minus'
                                    pressedIconName='add-plus'
                                    wrapText={false}
                                >
                                    {!searchCollapsed ? 'Hide Search Options' : 'Show Search Options'}
                                </ToggleButton>
                            </Styled.HeaderButtons>
                        </Styled.ContainerHeader>
                    }>
                        <Table
                            loading={loading}
                            columnDefinitions={columns}
                            items={items}
                            sortingDisabled
                            variant='container'
                            resizableColumns
                            pagination={<Pagination {...paginationProps} />}
                        />
                    </Container>
                </Styled.DashboardTable>

                {viewDetailsModalOpen && (
                    <ViewDetailsModal
                        loading={detailLoading}
                        visible={viewDetailsModalOpen}
                        onDismiss={() => setViewDetailsModalOpen(false)}
                        selectedReview={selectedReview}
                        userPermissions={userPermissions}
                        onEditReview={handleEditReview}
                        onDeleteReview={handleDeleteReview}
                        viewOnly={false}
                    />
                )}

                {selectedReview && (
                    <EditReviewModal
                        visible={editModalOpen}
                        onDismiss={() => setEditModalOpen(false)}
                        selectedReview={selectedReview}
                        org={org}
                        role={role}
                        user={user}
                        userPermissions={userPermissions}
                        onSubmit={handleCsqaFormEditSubmit}
                    />
                )}
            </Styled.DashboardBody>
        </Styled.DashboardWrapper>
    );
};

export default React.memo(CsqaDashboard);
